import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Plus, Save, Trash2, Activity, ClipboardList } from "lucide-react";
import { Card, Btn, Badge, Spinner, EmptyState } from "@/components/ui-kit";
import RosterViews from "@/components/RosterViews";
import ShiftEditModal from "@/components/ShiftEditModal";
import ShiftCheck, { useShiftCheck } from "@/components/ShiftCheck";
import api from "@/lib/api";
import { CycleBadge, sessionsLabel } from "@/components/Cycle";

const err = (e, m) => toast.error(e.response?.data?.detail || m);

function AddShift({ cid, patients, onDone }) {
  const [f, setF] = useState({ date: "", start: "09:00", end: "17:00", location: "Home visits", patient_id: "" });
  const add = async () => {
    if (!f.date) return toast.error("Pick a date");
    try { await api.post(`/ops/clinicians/${cid}/roster`, f); toast.success("Shift added"); setF({ ...f, date: "", patient_id: "" }); onDone(); }
    catch (e) { err(e, "Could not add shift"); }
  };
  const check = useShiftCheck({ clinicianId: cid, date: f.date, start: f.start, end: f.end, patientId: f.patient_id });
  return (
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-2 mb-4">
      <input type="date" className="pq-input py-2" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="as-shift-date" />
      <input type="time" className="pq-input py-2" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="as-shift-start" />
      <input type="time" className="pq-input py-2" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="as-shift-end" />
      <input className="pq-input py-2" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Location" data-testid="as-shift-location" />
      <select className="pq-input py-2" value={f.patient_id} onChange={(e) => setF({ ...f, patient_id: e.target.value })} data-testid="as-shift-patient">
        <option value="">General shift</option>
        {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <Btn variant="primary" onClick={add} data-testid="as-shift-add"><Plus size={14} /> Add shift</Btn>
      <div className="col-span-2 lg:col-span-6 -mt-1"><ShiftCheck data={check} prefix="as-shift-check" onPickDate={(d) => setF({ ...f, date: d.date, start: d.start, end: d.end })} /></div>
    </div>
  );
}

function LoggedEditor({ c, onDone }) {
  const oc = c.outcome || {};
  const [f, setF] = useState({ sessions_completed: oc.sessions_completed || 0, adherence: oc.adherence || 0, note: oc.note || "" });
  const save = async () => {
    try { await api.put(`/admin/referrals/${c.id}/logged`, f); toast.success("Logged sessions updated"); onDone(); }
    catch (e) { err(e, "Could not update"); }
  };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-[110px_110px_1fr_auto] gap-2 items-end">
      <label className="block"><span className="pq-label">Completed</span>
        <input type="number" min={0} max={oc.sessions_total > 0 ? oc.sessions_total : undefined} className="pq-input py-1.5 mt-1" value={f.sessions_completed}
          onChange={(e) => setF({ ...f, sessions_completed: e.target.value })} data-testid={`as-logged-count-${c.id}`} /></label>
      <label className="block"><span className="pq-label">Adherence %</span>
        <input type="number" min={0} max={100} className="pq-input py-1.5 mt-1" value={f.adherence}
          onChange={(e) => setF({ ...f, adherence: e.target.value })} data-testid={`as-logged-adherence-${c.id}`} /></label>
      <label className="block col-span-2 sm:col-span-1"><span className="pq-label">Latest note</span>
        <input className="pq-input py-1.5 mt-1" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid={`as-logged-note-${c.id}`} /></label>
      <Btn size="sm" variant="primary" onClick={save} data-testid={`as-logged-save-${c.id}`}><Save size={13} /> Save</Btn>
    </div>
  );
}

function ScheduledRow({ c, s, onDone }) {
  const [f, setF] = useState({ date: s.date || "", time: s.time || "", status: s.status || "Scheduled" });
  const save = async () => {
    try { await api.put(`/admin/referrals/${c.id}/schedule/${s.id}`, f); toast.success("Session updated"); onDone(); }
    catch (e) { err(e, "Could not update session"); }
  };
  const del = async () => {
    if (!window.confirm("Delete this scheduled session?")) return;
    try { await api.delete(`/admin/referrals/${c.id}/schedule/${s.id}`); toast.success("Session deleted"); onDone(); }
    catch (e) { err(e, "Could not delete"); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap bg-slate-50 rounded-lg px-2.5 py-2" data-testid={`as-sched-${s.id}`}>
      <input type="date" className="pq-input py-1 w-[150px]" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid={`as-sched-date-${s.id}`} />
      <input type="time" className="pq-input py-1 w-[110px]" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid={`as-sched-time-${s.id}`} />
      <select className="pq-input py-1 w-[140px]" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid={`as-sched-status-${s.id}`}>
        {["Scheduled", "Confirmed", "Completed", "Cancelled"].map((x) => <option key={x}>{x}</option>)}
      </select>
      <div className="flex gap-1.5 ml-auto">
        <Btn size="sm" onClick={save} data-testid={`as-sched-save-${s.id}`}><Save size={13} /> Save</Btn>
        <Btn size="sm" variant="danger" onClick={del} data-testid={`as-sched-del-${s.id}`}><Trash2 size={13} /></Btn>
      </div>
    </div>
  );
}

function AddScheduled({ c, onDone }) {
  const [f, setF] = useState({ date: "", time: "" });
  const add = async () => {
    if (!f.date) return toast.error("Pick a date");
    try { await api.post(`/referrals/${c.id}/schedule`, f); toast.success("Session scheduled"); setF({ date: "", time: "" }); onDone(); }
    catch (e) { err(e, "Could not schedule"); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap mt-2">
      <input type="date" className="pq-input py-1 w-[150px]" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid={`as-sched-new-date-${c.id}`} />
      <input type="time" className="pq-input py-1 w-[110px]" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid={`as-sched-new-time-${c.id}`} />
      <Btn size="sm" onClick={add} data-testid={`as-sched-add-${c.id}`}><Plus size={13} /> Add session</Btn>
    </div>
  );
}

function CheckinRow({ c, ck, onDone }) {
  const [f, setF] = useState({ pain_score: ck.pain_score ?? 0, note: ck.note || "" });
  const save = async () => {
    try { await api.put(`/admin/referrals/${c.id}/checkins/${ck.id}`, f); toast.success("Session log updated"); onDone(); }
    catch (e) { err(e, "Could not update"); }
  };
  const del = async () => {
    if (!window.confirm("Delete this session log entry?")) return;
    try { await api.delete(`/admin/referrals/${c.id}/checkins/${ck.id}`); toast.success("Entry deleted"); onDone(); }
    catch (e) { err(e, "Could not delete"); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap bg-slate-50 rounded-lg px-2.5 py-2" data-testid={`as-ck-${ck.id}`}>
      <span className="text-[11.5px] text-slate-500 w-[92px] shrink-0">{ck.date} · {ck.by === "patient" ? "Patient" : "Physio"}</span>
      <select className="pq-input py-1 w-[90px]" value={f.pain_score} onChange={(e) => setF({ ...f, pain_score: Number(e.target.value) })} data-testid={`as-ck-pain-${ck.id}`}>
        {Array.from({ length: 11 }, (_, n) => <option key={n} value={n}>{n}/10</option>)}
      </select>
      <input className="pq-input py-1 flex-1 min-w-[160px]" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note" data-testid={`as-ck-note-${ck.id}`} />
      <div className="flex gap-1.5">
        <Btn size="sm" onClick={save} data-testid={`as-ck-save-${ck.id}`}><Save size={13} /></Btn>
        <Btn size="sm" variant="danger" onClick={del} data-testid={`as-ck-del-${ck.id}`}><Trash2 size={13} /></Btn>
      </div>
    </div>
  );
}

function CaseSessions({ c, onDone }) {
  const oc = c.outcome || {};
  return (
    <Card className="p-5" data-testid={`as-case-${c.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="min-w-0">
          <div className="font-semibold text-[14px] truncate">{c.patient_name} <span className="text-slate-400 font-normal text-[12px]">· {c.ref_no}</span></div>
          <CycleBadge cycle={c.cycle} testid={`as-cycle-${c.id}`} />
          <div className="text-[12.5px] text-slate-500 truncate">{c.diagnosis_label}</div>
        </div>
        <Badge tone="teal">{sessionsLabel(oc)} · {oc.progress || 0}%</Badge>
      </div>
      <div className="pq-label mb-1.5 flex items-center gap-1.5"><Activity size={12} /> Logged sessions</div>
      <LoggedEditor key={`${c.id}-${oc.sessions_completed}-${oc.adherence}`} c={c} onDone={onDone} />
      {(c.checkins || []).length > 0 && (
        <div className="space-y-1.5 mt-3">
          {[...c.checkins].reverse().map((ck) => <CheckinRow key={`${ck.id}-${ck.pain_score}-${ck.note}`} c={c} ck={ck} onDone={onDone} />)}
        </div>
      )}
      <div className="pq-label mt-4 mb-1.5 flex items-center gap-1.5"><CalendarDays size={12} /> Scheduled sessions</div>
      {(c.schedule || []).length === 0 ? <p className="text-[12px] text-slate-400">No sessions scheduled on this case.</p> : (
        <div className="space-y-1.5">
          {c.schedule.map((s) => <ScheduledRow key={`${s.id}-${s.date}-${s.time}-${s.status}`} c={c} s={s} onDone={onDone} />)}
        </div>
      )}
      <AddScheduled c={c} onDone={onDone} />
    </Card>
  );
}

export default function AdminSessions() {
  const [clinicians, setClinicians] = useState(null);
  const [cid, setCid] = useState("");
  const [data, setData] = useState(null);
  const [editShift, setEditShift] = useState(null);
  useEffect(() => { api.get("/ops/clinicians").then((r) => { setClinicians(r.data); if (r.data[0]) setCid(r.data[0].id); }); }, []);
  const load = useCallback(() => { if (cid) api.get(`/admin/therapists/${cid}/sessions`).then((r) => setData(r.data)); }, [cid]);
  useEffect(() => { setData(null); load(); }, [load]);

  const delShift = async (s) => {
    if (!window.confirm(`Delete the ${s.date} ${s.start} shift?`)) return;
    await api.delete(`/ops/roster/${s.id}`); toast.success("Shift deleted"); load();
  };

  if (!clinicians) return <div className="flex justify-center py-20"><Spinner className="w-7 h-7 text-teal-600" /></div>;
  const cur = clinicians.find((c) => c.id === cid);
  if (clinicians.length === 0) return <Card><EmptyState icon={<ClipboardList size={24} />} title="No therapists yet" subtitle="Therapists appear here once they sign up." /></Card>;

  return (
    <div className="space-y-4" data-testid="admin-sessions">
      <Card className="p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display font-semibold text-[15px]">Therapist sessions</h3>
            <p className="text-[12.5px] text-slate-500">Pick a therapist to edit their roster shifts, scheduled case sessions and logged sessions.</p>
          </div>
          <select className="pq-input py-2 w-full sm:w-72" value={cid} onChange={(e) => setCid(e.target.value)} data-testid="as-therapist-select">
            {clinicians.map((c) => <option key={c.id} value={c.id}>{`${c.name}${c.city ? ` · ${c.city}` : ""}${c.account_suspended ? " (suspended)" : c.active === false ? " (inactive)" : ""}`}</option>)}
          </select>
        </div>
      </Card>
      {!data ? <div className="flex justify-center py-12"><Spinner className="w-6 h-6 text-teal-600" /></div> : (
        <>
          <Card className="p-5" data-testid="as-roster">
            <div className="font-semibold text-[14px] mb-3 flex items-center gap-2"><CalendarDays size={16} className="text-teal-600" /> Roster shifts — {data.clinician.name}</div>
            {cur && (cur.active === false || cur.account_suspended)
              ? <p className="text-[12px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-4" data-testid="as-unavailable">This therapist is {cur.account_suspended ? "suspended" : "inactive"} — new shifts can't be added. Existing shifts can still be edited or removed.</p>
              : <AddShift cid={cid} patients={data.patients} onDone={load} />}
            <RosterViews shifts={data.shifts} onEdit={setEditShift} onDelete={delShift} prefix="as-roster" />
          </Card>
          {data.cases.length === 0 ? <Card><EmptyState icon={<Activity size={24} />} title="No assigned cases" subtitle="This therapist has no cases yet." /></Card>
            : data.cases.map((c) => <CaseSessions key={c.id} c={c} onDone={load} />)}
        </>
      )}
      {editShift && <ShiftEditModal shift={editShift} patients={data?.patients || []} onClose={() => setEditShift(null)} onSaved={() => { setEditShift(null); load(); }} />}
    </div>
  );
}
